import { logger } from './logger';

export type EnergyLevel = 'low' | 'medium' | 'high';

export interface EnergyContext {
  energyLevel: EnergyLevel;
  contextDescription?: string;
  availableTimeMinutes?: number;
  timestamp: Date;
  location?: string;
  mood?: string;
  stressLevel?: number; // 1-10 scale
  distractionLevel?: number; // 1-10 scale
  motivationLevel?: number; // 1-10 scale
}

export interface EnergyPattern {
  hour: number;
  dayOfWeek: number; // 0-6 (Sunday to Saturday)
  averageEnergyLevel: EnergyLevel;
  frequency: number;
  contexts: string[];
}

export interface EnergyInsights {
  currentContext: EnergyContext | null;
  averageEnergyByHour: { [hour: number]: EnergyLevel };
  mostProductiveHours: number[];
  leastProductiveHours: number[];
  bestContexts: string[];
  energyTrends: {
    improving: boolean;
    consistentLow: boolean;
    peakHours: number[];
  };
  recommendations: string[];
}

export class EnergyContextService {
  private currentContext: EnergyContext | null = null;
  private contextHistoryKey = 'adhd-energy-context-history';
  private currentContextKey = 'adhd-current-energy-context';
  private maxHistoryEntries = 500;

  private onContextChangeCallback?: (context: EnergyContext) => void;

  constructor() {
    this.loadCurrentContext();
  }

  /**
   * Set current energy context
   */
  setEnergyContext(
    energyLevel: EnergyLevel,
    options?: {
      contextDescription?: string;
      availableTimeMinutes?: number;
      location?: string;
      mood?: string;
      stressLevel?: number;
      distractionLevel?: number;
      motivationLevel?: number;
    }
  ): { success: boolean; context: EnergyContext; message: string } {
    const context: EnergyContext = {
      energyLevel,
      timestamp: new Date(),
      contextDescription: options?.contextDescription,
      availableTimeMinutes: options?.availableTimeMinutes,
      location: options?.location,
      mood: options?.mood,
      stressLevel: options?.stressLevel,
      distractionLevel: options?.distractionLevel,
      motivationLevel: options?.motivationLevel
    };

    this.currentContext = context;
    this.saveCurrentContext();
    this.addToHistory(context);

    logger.adhdAction('energy_context_set', {
      energyLevel,
      contextDescription: options?.contextDescription,
      availableTimeMinutes: options?.availableTimeMinutes,
      location: options?.location,
      mood: options?.mood,
      stressLevel: options?.stressLevel,
      distractionLevel: options?.distractionLevel,
      motivationLevel: options?.motivationLevel
    });

    // Notify callback if set
    if (this.onContextChangeCallback) {
      this.onContextChangeCallback(context);
    }

    const message = `Energy context set to ${energyLevel}${
      options?.contextDescription ? ` in ${options.contextDescription}` : ''
    }${
      options?.availableTimeMinutes ? ` with ${options.availableTimeMinutes} minutes available` : ''
    }. This will improve task recommendations.`;

    return {
      success: true,
      context,
      message
    };
  }

  /**
   * Get current energy context
   */
  getCurrentContext(): EnergyContext | null {
    // Check if current context is still recent (within 4 hours)
    if (this.currentContext) {
      const age = Date.now() - this.currentContext.timestamp.getTime();
      const maxAge = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
      
      if (age > maxAge) {
        // Context is stale, estimate based on time of day
        this.currentContext = this.estimateEnergyFromTime();
        this.saveCurrentContext();
      }
    } else {
      // No current context, estimate based on time of day and history
      this.currentContext = this.estimateEnergyFromTime();
      this.saveCurrentContext();
    }

    return this.currentContext;
  }

  /**
   * Update specific aspects of current context
   */
  updateCurrentContext(updates: Partial<Omit<EnergyContext, 'timestamp'>>): EnergyContext | null {
    if (!this.currentContext) {
      return null;
    }

    this.currentContext = {
      ...this.currentContext,
      ...updates,
      timestamp: new Date() // Update timestamp
    };

    this.saveCurrentContext();
    this.addToHistory(this.currentContext);

    logger.adhdAction('energy_context_updated', {
      updates: Object.keys(updates)
    });

    if (this.onContextChangeCallback) {
      this.onContextChangeCallback(this.currentContext);
    }

    return this.currentContext;
  }

  /**
   * Get energy insights based on historical data
   */
  getEnergyInsights(): EnergyInsights {
    const history = this.getHistory();
    const currentContext = this.getCurrentContext();

    if (history.length < 5) {
      return {
        currentContext,
        averageEnergyByHour: {},
        mostProductiveHours: [],
        leastProductiveHours: [],
        bestContexts: [],
        energyTrends: {
          improving: false,
          consistentLow: false,
          peakHours: []
        },
        recommendations: ['Collect more energy data by setting your context throughout the day to get personalized insights.']
      };
    }

    // Calculate average energy by hour
    const energyByHour: { [hour: number]: EnergyLevel[] } = {};
    const contextCounts: { [context: string]: number } = {};

    history.forEach(entry => {
      const hour = entry.timestamp.getHours();
      if (!energyByHour[hour]) {
        energyByHour[hour] = [];
      }
      energyByHour[hour].push(entry.energyLevel);

      if (entry.contextDescription) {
        contextCounts[entry.contextDescription] = (contextCounts[entry.contextDescription] || 0) + 1;
      }
    });

    // Calculate most common energy level per hour
    const averageEnergyByHour: { [hour: number]: EnergyLevel } = {};
    Object.entries(energyByHour).forEach(([hour, levels]) => {
      const levelCounts: { [level in EnergyLevel]: number } = { low: 0, medium: 0, high: 0 };
      levels.forEach(level => levelCounts[level]++);
      
      const mostCommon = Object.entries(levelCounts)
        .sort(([,a], [,b]) => b - a)[0][0] as EnergyLevel;
      
      averageEnergyByHour[parseInt(hour)] = mostCommon;
    });

    // Find most and least productive hours
    const productiveHours = Object.entries(averageEnergyByHour)
      .filter(([,level]) => level === 'high')
      .map(([hour]) => parseInt(hour))
      .sort((a, b) => a - b);

    const leastProductiveHours = Object.entries(averageEnergyByHour)
      .filter(([,level]) => level === 'low')
      .map(([hour]) => parseInt(hour))
      .sort((a, b) => a - b);

    // Best contexts
    const bestContexts = Object.entries(contextCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([context]) => context);

    // Energy trends analysis
    const recent = history.slice(-20); // Last 20 entries
    const older = history.slice(-40, -20); // Previous 20 entries
    
    const energyLevelScore = (level: EnergyLevel): number => {
      switch (level) {
        case 'low': return 1;
        case 'medium': return 2;
        case 'high': return 3;
      }
    };

    const recentAvg = recent.length > 0 
      ? recent.reduce((sum, entry) => sum + energyLevelScore(entry.energyLevel), 0) / recent.length
      : 0;
    
    const olderAvg = older.length > 0
      ? older.reduce((sum, entry) => sum + energyLevelScore(entry.energyLevel), 0) / older.length
      : recentAvg;

    const improving = recentAvg > olderAvg;
    const consistentLow = recent.length > 10 && recentAvg < 1.5;

    // Generate recommendations
    const recommendations = this.generateEnergyRecommendations({
      currentContext,
      averageEnergyByHour,
      mostProductiveHours: productiveHours,
      leastProductiveHours,
      bestContexts,
      improving,
      consistentLow
    });

    return {
      currentContext,
      averageEnergyByHour,
      mostProductiveHours: productiveHours,
      leastProductiveHours,
      bestContexts,
      energyTrends: {
        improving,
        consistentLow,
        peakHours: productiveHours
      },
      recommendations
    };
  }

  /**
   * Get the best time slots for specific energy requirements
   */
  getBestTimeSlotsForEnergy(requiredEnergy: EnergyLevel): number[] {
    const insights = this.getEnergyInsights();
    
    return Object.entries(insights.averageEnergyByHour)
      .filter(([,level]) => {
        if (requiredEnergy === 'high') return level === 'high';
        if (requiredEnergy === 'medium') return level === 'medium' || level === 'high';
        return true; // Low energy tasks can be done anytime
      })
      .map(([hour]) => parseInt(hour))
      .sort((a, b) => a - b);
  }

  /**
   * Set callback for context changes
   */
  onContextChange(callback: (context: EnergyContext) => void): void {
    this.onContextChangeCallback = callback;
  }

  /**
   * Get energy statistics
   */
  getEnergyStats(): {
    totalEntries: number;
    averageEnergyScore: number;
    mostCommonLevel: EnergyLevel;
    bestProductivityWindow: string;
    consistencyScore: number;
  } {
    const history = this.getHistory();
    
    if (history.length === 0) {
      return {
        totalEntries: 0,
        averageEnergyScore: 0,
        mostCommonLevel: 'medium',
        bestProductivityWindow: 'No data',
        consistencyScore: 0
      };
    }

    const energyLevelScore = (level: EnergyLevel): number => {
      switch (level) {
        case 'low': return 1;
        case 'medium': return 2;
        case 'high': return 3;
      }
    };

    const totalScore = history.reduce((sum, entry) => sum + energyLevelScore(entry.energyLevel), 0);
    const averageEnergyScore = totalScore / history.length;

    // Find most common level
    const levelCounts: { [level in EnergyLevel]: number } = { low: 0, medium: 0, high: 0 };
    history.forEach(entry => levelCounts[entry.energyLevel]++);
    const mostCommonLevel = Object.entries(levelCounts)
      .sort(([,a], [,b]) => b - a)[0][0] as EnergyLevel;

    // Find best productivity window
    const insights = this.getEnergyInsights();
    const bestWindow = insights.mostProductiveHours.length > 0 
      ? `${insights.mostProductiveHours[0]}:00 - ${insights.mostProductiveHours[insights.mostProductiveHours.length - 1] + 1}:00`
      : 'No clear pattern';

    // Calculate consistency score (how predictable energy levels are)
    const hourlyVariance = Object.values(insights.averageEnergyByHour).length;
    const consistencyScore = Math.max(0, 100 - (hourlyVariance * 10));

    return {
      totalEntries: history.length,
      averageEnergyScore: Math.round(averageEnergyScore * 100) / 100,
      mostCommonLevel,
      bestProductivityWindow: bestWindow,
      consistencyScore: Math.round(consistencyScore)
    };
  }

  private estimateEnergyFromTime(): EnergyContext {
    const now = new Date();
    const hour = now.getHours();
    
    // Default energy patterns based on research
    let estimatedLevel: EnergyLevel;
    
    if (hour >= 9 && hour <= 11) {
      estimatedLevel = 'high'; // Morning peak
    } else if (hour >= 14 && hour <= 16) {
      estimatedLevel = 'medium'; // Afternoon moderate
    } else if (hour >= 22 || hour <= 6) {
      estimatedLevel = 'low'; // Night/early morning low
    } else {
      estimatedLevel = 'medium'; // Default
    }

    // Check historical data for this hour if available
    const history = this.getHistory();
    const thisHourHistory = history.filter(entry => entry.timestamp.getHours() === hour);
    
    if (thisHourHistory.length >= 3) {
      const levelCounts: { [level in EnergyLevel]: number } = { low: 0, medium: 0, high: 0 };
      thisHourHistory.forEach(entry => levelCounts[entry.energyLevel]++);
      
      const historicalLevel = Object.entries(levelCounts)
        .sort(([,a], [,b]) => b - a)[0][0] as EnergyLevel;
      
      estimatedLevel = historicalLevel;
    }

    return {
      energyLevel: estimatedLevel,
      timestamp: now,
      contextDescription: 'Estimated based on time patterns'
    };
  }

  private generateEnergyRecommendations(data: {
    currentContext: EnergyContext | null;
    averageEnergyByHour: { [hour: number]: EnergyLevel };
    mostProductiveHours: number[];
    leastProductiveHours: number[];
    bestContexts: string[];
    improving: boolean;
    consistentLow: boolean;
  }): string[] {
    const recommendations: string[] = [];
    const now = new Date();
    const currentHour = now.getHours();

    // Current context recommendations
    if (data.currentContext) {
      if (data.currentContext.energyLevel === 'low') {
        recommendations.push('You\'re in a low energy state. Consider simple, routine tasks or take a short break.');
      } else if (data.currentContext.energyLevel === 'high') {
        recommendations.push('Great energy level! This is perfect for tackling complex or important tasks.');
      }

      if (data.currentContext.distractionLevel && data.currentContext.distractionLevel > 7) {
        recommendations.push('High distraction level detected. Consider changing your environment or using focus techniques.');
      }

      if (data.currentContext.stressLevel && data.currentContext.stressLevel > 7) {
        recommendations.push('High stress detected. Take deep breaths and consider breaking tasks into smaller steps.');
      }
    }

    // Time-based recommendations
    if (data.mostProductiveHours.length > 0) {
      const nextProductiveHour = data.mostProductiveHours.find(hour => hour > currentHour);
      if (nextProductiveHour) {
        recommendations.push(`Your next high-energy window is typically around ${nextProductiveHour}:00. Plan important work for then.`);
      }
    }

    if (data.leastProductiveHours.includes(currentHour)) {
      recommendations.push('This is typically a low-energy time for you. Consider routine tasks or taking a break.');
    }

    // Context recommendations
    if (data.bestContexts.length > 0) {
      recommendations.push(`You tend to perform best in: ${data.bestContexts.slice(0, 3).join(', ')}.`);
    }

    // Trend recommendations
    if (data.consistentLow) {
      recommendations.push('Your energy has been consistently low. Consider reviewing your sleep, nutrition, and stress levels.');
      recommendations.push('Break larger tasks into smaller, more manageable pieces during low-energy periods.');
    } else if (data.improving) {
      recommendations.push('Your energy levels are improving! Great job maintaining positive patterns.');
    }

    // General ADHD-friendly recommendations
    if (currentHour >= 9 && currentHour <= 11 && !data.mostProductiveHours.includes(currentHour)) {
      recommendations.push('This is typically a peak energy time for many people. Consider important tasks now.');
    }

    if (recommendations.length === 0) {
      recommendations.push('Keep tracking your energy context to get more personalized recommendations.');
    }

    return recommendations;
  }

  private saveCurrentContext(): void {
    if (this.currentContext) {
      localStorage.setItem(this.currentContextKey, JSON.stringify(this.currentContext));
    }
  }

  private loadCurrentContext(): void {
    try {
      const stored = localStorage.getItem(this.currentContextKey);
      if (stored) {
        const context = JSON.parse(stored);
        this.currentContext = {
          ...context,
          timestamp: new Date(context.timestamp)
        };
      }
    } catch (error) {
      console.error('Failed to load current energy context:', error);
      this.currentContext = null;
    }
  }

  private addToHistory(context: EnergyContext): void {
    try {
      const history = this.getHistory();
      history.push(context);
      
      // Keep only the most recent entries
      const trimmed = history.slice(-this.maxHistoryEntries);
      localStorage.setItem(this.contextHistoryKey, JSON.stringify(trimmed));
    } catch (error) {
      console.error('Failed to save energy context to history:', error);
    }
  }

  private getHistory(): EnergyContext[] {
    try {
      const stored = localStorage.getItem(this.contextHistoryKey);
      if (stored) {
        const history = JSON.parse(stored);
        return history.map((entry: any) => ({
          ...entry,
          timestamp: new Date(entry.timestamp)
        }));
      }
    } catch (error) {
      console.error('Failed to load energy context history:', error);
    }
    return [];
  }
}

// Create singleton instance
export const energyContextService = new EnergyContextService();